import React, { useEffect, useState, useCallback } from "react";
import { 
  getInventory, 
  uploadInventory, 
  downloadInventory,
  updateInventoryItem,
  getBrands,
  syncShopify,
  getInventoryAuditLog,
  api
} from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { 
  Upload, 
  Download, 
  Package, 
  Search,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  ShoppingBag,
  Tag,
  Box,
  Settings2,
  CloudDownload,
  Pencil,
  Trash2,
  Square,
  CheckSquare,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  RotateCcw,
  Clock,
  ArrowDownToLine,
  ArrowUpFromLine
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const YEARS = ["24", "25", "26", "27", "28"];



function ShopifySyncPanel({ onSyncComplete, inventory }) {
  const [expanded, setExpanded] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [rtoLogs, setRtoLogs] = useState([]);
  const [rtoModel, setRtoModel] = useState("");
  const [rtoQty, setRtoQty] = useState(1);
  const [rtoNote, setRtoNote] = useState("");
  const [addingRto, setAddingRto] = useState(false);

  const loadLogs = async () => {
    try {
      const rtoR = await axios.get(`${API}/shopify-sync/rto-log?limit=30`);
      setRtoLogs(rtoR.data || []);
    } catch {}
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${API}/shopify-sync/export`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `shopify_inventory_${new Date().toISOString().split("T")[0]}.csv`; a.click(); URL.revokeObjectURL(url);
      toast.success("Shopify CSV exported");
    } catch (err) { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  const handleAddRto = async () => {
    if (!rtoModel) { toast.error("Select a product"); return; }
    setAddingRto(true);
    try {
      const r = await axios.post(`${API}/shopify-sync/rto-add`, { model_name: rtoModel, quantity: rtoQty, note: rtoNote || "RTO received at PO warehouse" });
      toast.success(`Added ${rtoQty}× ${rtoModel} → new qty: ${r.data.new_quantity}`);
      setRtoModel(""); setRtoQty(1); setRtoNote("");
      onSyncComplete();
      await loadLogs();
    } catch (err) { toast.error(err.response?.data?.detail || "RTO add failed"); }
    finally { setAddingRto(false); }
  };

  return (
    <div className="mb-4">
      <button onClick={() => { setExpanded(!expanded); if (!expanded) loadLogs(); }}
        className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition-colors"
        data-testid="shopify-sync-toggle">
        <span className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
          <Download className="w-4 h-4" /> Shopify Inventory Sync
        </span>
        {expanded ? <ChevronUp className="w-4 h-4 text-emerald-600" /> : <ChevronDown className="w-4 h-4 text-emerald-600" />}
      </button>
      {expanded && (
        <div className="mt-2 p-4 rounded-lg border border-emerald-100 bg-white space-y-4">
          {/* Export to Shopify */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h4 className="text-xs font-bold text-blue-700 uppercase mb-1 flex items-center gap-1.5">
              <ArrowUpFromLine className="w-3.5 h-3.5" /> Export to Shopify
            </h4>
            <p className="text-xs text-blue-600/70 mb-3">Download app inventory as Shopify-formatted CSV. Upload it to Shopify to sync.</p>
            <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting}
              className="border-blue-300 text-blue-700 hover:bg-blue-100 w-full">
              {exporting ? <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />Exporting...</> : <><Download className="w-3.5 h-3.5 mr-1.5" />Download for Shopify</>}
            </Button>
          </div>

          {/* RTO Addition */}
          <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
            <h4 className="text-xs font-bold text-amber-700 uppercase mb-1 flex items-center gap-1.5">
              <RotateCcw className="w-3 h-3" /> RTO → Add to PO Warehouse
            </h4>
            <div className="flex items-center gap-2 mt-2">
              <select value={rtoModel} onChange={e => setRtoModel(e.target.value)}
                className="h-8 text-xs border rounded px-2 flex-1" data-testid="rto-product-select">
                <option value="">Select product...</option>
                {inventory.filter(i => i.brand === "OBLIK").map(i => (
                  <option key={i.model_name} value={i.model_name}>{i.model_name} ({i.quantity})</option>
                ))}
              </select>
              <input type="number" min="1" value={rtoQty} onChange={e => setRtoQty(parseInt(e.target.value) || 1)}
                className="h-8 w-16 text-xs border rounded px-2 text-center" />
              <input value={rtoNote} onChange={e => setRtoNote(e.target.value)}
                className="h-8 text-xs border rounded px-2 flex-1" placeholder="Note (optional)" />
              <Button size="sm" onClick={handleAddRto} disabled={addingRto} className="bg-amber-600 hover:bg-amber-700 text-white h-8"
                data-testid="rto-add-btn">
                {addingRto ? "Adding..." : "+ Add"}
              </Button>
            </div>
            {rtoLogs.length > 0 && (
              <div className="mt-3 max-h-32 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-amber-100/50 sticky top-0"><tr><th className="px-2 py-1 text-left">Date</th><th className="px-2 py-1 text-left">Product</th><th className="px-2 py-1 text-right">+Qty</th><th className="px-2 py-1 text-right">New</th><th className="px-2 py-1 text-left">Note</th></tr></thead>
                  <tbody className="divide-y">
                    {rtoLogs.map((l, i) => (
                      <tr key={i}>
                        <td className="px-2 py-1 text-slate-500">{l.timestamp ? new Date(l.timestamp).toLocaleDateString('en-IN', {day:'2-digit',month:'short'}) : ''}</td>
                        <td className="px-2 py-1 font-medium">{l.model_name}</td>
                        <td className="px-2 py-1 text-right text-green-600">+{l.quantity_added}</td>
                        <td className="px-2 py-1 text-right">{l.new_quantity}</td>
                        <td className="px-2 py-1 text-slate-400">{l.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


export default function Inventory() {
  const [inventory, setInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [brands, setBrands] = useState(["OBLIK"]);
  const [selectedBrand, setSelectedBrand] = useState("all");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [stockFilter, setStockFilter] = useState("all");
  const [selectedItem, setSelectedItem] = useState(null);
  const [importMonth, setImportMonth] = useState("02");
  const [importYear, setImportYear] = useState("26");
  const [editMode, setEditMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [expandedAudit, setExpandedAudit] = useState(null); // model_name of expanded row
  const [auditLogs, setAuditLogs] = useState({}); // { model_name: [logs] }

  const toggleAuditLog = async (modelName) => {
    if (expandedAudit === modelName) {
      setExpandedAudit(null);
      return;
    }
    setExpandedAudit(modelName);
    if (!auditLogs[modelName]) {
      try {
        const res = await getInventoryAuditLog(modelName);
        setAuditLogs(prev => ({ ...prev, [modelName]: res.data.logs || [] }));
      } catch {
        setAuditLogs(prev => ({ ...prev, [modelName]: [] }));
      }
    }
  };

  useEffect(() => {
    loadBrands();
    loadInventory();
  }, []);

  const filterInventory = useCallback(() => {
    let filtered = [...inventory];
    
    if (selectedBrand && selectedBrand !== "all") {
      filtered = filtered.filter(item => item.brand === selectedBrand);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.model_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (stockFilter === "low") {
      filtered = filtered.filter(item => item.quantity > 0 && item.quantity <= 10);
    } else if (stockFilter === "out") {
      filtered = filtered.filter(item => item.quantity === 0);
    }
    
    setFilteredInventory(filtered);
  }, [inventory, selectedBrand, searchTerm, stockFilter]);

  useEffect(() => {
    filterInventory();
  }, [filterInventory]);

  const loadBrands = async () => {
    try {
      const response = await getBrands();
      if (response.data && response.data.length > 0) {
        setBrands(response.data);
      }
    } catch (error) {
      console.error("Failed to load brands");
    }
  };


  const loadInventory = async () => {
    try {
      const response = await getInventory();
      setInventory(response.data);
    } catch (error) {
      toast.error("Failed to load inventory");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const response = await uploadInventory(file);
      toast.success(`Uploaded ${response.data.count} items for brand: ${response.data.brand}`);
      await loadBrands();
      await loadInventory();
    } catch (error) {
      toast.error("Failed to upload inventory");
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await downloadInventory();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'inventory.csv';
      a.click();
      toast.success("Inventory downloaded");
    } catch (error) {
      toast.error("Failed to download inventory");
    }
  };

  const handleShopifySync = async () => {
    setSyncing(true);
    try {
      const response = await syncShopify();
      if (response.data.status === 'success') {
        toast.success(`Synced ${response.data.orders_processed} Shopify orders`);
        await loadInventory();
      } else {
        toast.error(response.data.message || "Sync failed");
      }
    } catch (error) {
      toast.error("Shopify sync not configured");
    } finally {
      setSyncing(false);
    }
  };

  const handleQuantityUpdate = async (modelName, newQuantity) => {
    const qty = parseInt(newQuantity);
    if (isNaN(qty) || qty < 0) return;
    const currentItem = inventory.find(i => i.model_name === modelName);
    if (currentItem && currentItem.quantity === qty) return;
    try {
      await api.put(`/inventory/by-name/${encodeURIComponent(modelName)}`, { quantity: qty });
      toast.success("Quantity updated");
      await loadInventory();
      if (expandedAudit) {
        const res = await getInventoryAuditLog(expandedAudit);
        setAuditLogs(prev => ({ ...prev, [expandedAudit]: res.data.logs || [] }));
      }
    } catch (error) {
      toast.error("Failed to update quantity");
    }
  };

  const handlePrintBoxTag = async (item) => {
    const month = item.import_month || `${importMonth}/${importYear}`;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/inventory/${item.sr_no}/box-tag?import_month=${encodeURIComponent(month)}`;
    window.open(url, '_blank');
  };

  const handlePrintProductTag = async (item) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/inventory/${item.sr_no}/product-tag`;
    window.open(url, '_blank');
  };

  const handleUpdateImportMonth = async () => {
    if (!selectedItem) return;
    try {
      await api.put(`/inventory/${selectedItem.sr_no}/import-month`, {
        import_month: `${importMonth}/${importYear}`
      });
      toast.success("Import month updated");
      await loadInventory();
      setSelectedItem(null);
    } catch (error) {
      toast.error("Failed to update import month");
    }
  };

  const toggleSelectItem = (srNo) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(srNo)) next.delete(srNo);
      else next.add(srNo);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === filteredInventory.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredInventory.map(i => i.sr_no)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) return;
    setDeleting(true);
    try {
      await api.post('/inventory/bulk-delete', { sr_nos: [...selectedItems] });
      toast.success(`Deleted ${selectedItems.size} items`);
      setSelectedItems(new Set());
      setEditMode(false);
      setShowDeleteConfirm(false);
      await loadInventory();
    } catch (error) {
      toast.error("Failed to delete items");
    } finally {
      setDeleting(false);
    }
  };

  const getStockStatus = (quantity) => {
    if (quantity === 0) {
      return <Badge className="bg-red-100 text-red-700">Out</Badge>;
    } else if (quantity <= 10) {
      return <Badge className="bg-amber-100 text-amber-700">Low</Badge>;
    }
    return <Badge className="bg-emerald-100 text-emerald-700">OK</Badge>;
  };

  const getStats = (brandFilter = "all") => {
    const items = brandFilter === "all" ? inventory : inventory.filter(i => i.brand === brandFilter);
    return {
      total: items.length,
      totalStock: items.reduce((sum, item) => sum + (item.quantity || 0), 0),
      lowStock: items.filter(item => item.quantity > 0 && item.quantity <= 10).length,
      outOfStock: items.filter(item => item.quantity === 0).length
    };
  };

  const stats = getStats(selectedBrand);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="inventory-page">
      {/* Shopify Inventory Sync */}
      <ShopifySyncPanel onSyncComplete={loadInventory} inventory={inventory} />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold font-mono">{stats.total}</p>
                <p className="text-xs text-slate-500">Total SKUs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-emerald-500" />
              <div>
                <p className="text-2xl font-bold font-mono">{stats.totalStock}</p>
                <p className="text-xs text-slate-500">Total Units</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-amber-500" />
              <div>
                <p className="text-2xl font-bold font-mono">{stats.lowStock}</p>
                <p className="text-xs text-slate-500">Low Stock</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-red-500" />
              <div>
                <p className="text-2xl font-bold font-mono">{stats.outOfStock}</p>
                <p className="text-xs text-slate-500">Out of Stock</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant={editMode ? "destructive" : "outline"}
                size="sm"
                onClick={() => { setEditMode(!editMode); setSelectedItems(new Set()); }}
                data-testid="edit-mode-toggle"
              >
                <Pencil className="w-4 h-4 mr-1" />
                {editMode ? "Exit Edit" : "Edit Mode"}
              </Button>
              {editMode && selectedItems.size > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={deleting}
                  data-testid="delete-selected-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete ({selectedItems.size})
                </Button>
              )}
              <Button
                variant={stockFilter === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setStockFilter("all")}
              >
                All ({stats.total})
              </Button>
              <Button
                variant={stockFilter === "low" ? "default" : "outline"}
                size="sm"
                onClick={() => setStockFilter("low")}
              >
                Low ({stats.lowStock})
              </Button>
              <Button
                variant={stockFilter === "out" ? "default" : "outline"}
                size="sm"
                onClick={() => setStockFilter("out")}
              >
                Out ({stats.outOfStock})
              </Button>
            </div>
            
            <div className="flex items-center gap-2 w-full md:w-auto flex-wrap">
              <div className="relative flex-1 md:w-48">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search..."
                  className="pl-9"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              
              <Button variant="outline" size="sm" onClick={handleDownload}>
                <Download className="w-4 h-4 mr-1" />Export
              </Button>
              
              <div className="relative">
                <Input type="file" accept=".csv,.xlsx,.xls" className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleUpload} disabled={uploading} />
                <Button disabled={uploading} size="sm">
                  {uploading ? <div className="spinner w-4 h-4 mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                  Import
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>


      {/* Brand Tabs */}
      <Tabs defaultValue="all" value={selectedBrand} onValueChange={setSelectedBrand}>
        <TabsList className="mb-4">
          <TabsTrigger value="all">All Brands</TabsTrigger>
          {brands.map(brand => (
            <TabsTrigger key={brand} value={brand}>{brand}</TabsTrigger>
          ))}
        </TabsList>

        {/* Inventory table */}
        <Card>
          <CardHeader className="py-3 px-4 border-b">
            <CardTitle className="text-sm font-medium">
              Inventory Items ({filteredInventory.length})
              {selectedBrand !== "all" && <Badge variant="secondary" className="ml-2">{selectedBrand}</Badge>}
            </CardTitle>
          </CardHeader>
          <ScrollArea className="h-[450px]">
            {filteredInventory.length === 0 ? (
              <div className="p-12 text-center text-slate-500">
                <Package className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p className="text-lg">No inventory items found</p>
              </div>
            ) : (
              <Table className="data-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    {editMode && (
                      <TableHead className="w-10 text-center">
                        <button onClick={toggleSelectAll} className="p-1" data-testid="select-all-checkbox">
                          {selectedItems.size === filteredInventory.length && filteredInventory.length > 0
                            ? <CheckSquare className="w-4 h-4 text-blue-600" />
                            : <Square className="w-4 h-4 text-slate-400" />}
                        </button>
                      </TableHead>
                    )}
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase w-12">#</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Product</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center w-20">Qty</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase text-right w-24">MRP</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center w-16">Status</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center w-32">Tags</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInventory.map((item) => (
                    <React.Fragment key={item.sr_no}>
                    <TableRow 
                      className={`${item.quantity === 0 ? 'bg-red-50' : ''} ${item.quantity > 0 && item.quantity <= 10 ? 'bg-amber-50' : ''} ${selectedItems.has(item.sr_no) ? 'bg-blue-50' : ''}`}
                    >
                      {editMode && (
                        <TableCell className="text-center">
                          <button onClick={() => toggleSelectItem(item.sr_no)} className="p-1" data-testid={`select-item-${item.sr_no}`}>
                            {selectedItems.has(item.sr_no)
                              ? <CheckSquare className="w-4 h-4 text-blue-600" />
                              : <Square className="w-4 h-4 text-slate-400" />}
                          </button>
                        </TableCell>
                      )}
                      <TableCell className="font-mono text-sm">{item.sr_no}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => toggleAuditLog(item.model_name)}
                            className="text-slate-400 hover:text-slate-700 transition-colors flex-shrink-0"
                            title="View inventory movement log"
                            data-testid={`audit-toggle-${item.sr_no}`}
                          >
                            {expandedAudit === item.model_name
                              ? <ChevronDown className="w-3.5 h-3.5" />
                              : <ChevronRight className="w-3.5 h-3.5" />}
                          </button>
                          <div>
                            <p className="font-medium text-sm">{item.model_name}</p>
                            <p className="text-xs text-slate-500">{item.brand || 'OBLIK'}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Input
                          type="number"
                          className="w-16 h-7 text-center font-mono text-sm mx-auto"
                          defaultValue={item.quantity}
                          key={`qty-${item.model_name}-${item.quantity}`}
                          onBlur={(e) => handleQuantityUpdate(item.model_name, e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); } }}
                          min="0"
                        />
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        Rs. {item.mrp?.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-center">
                        {getStockStatus(item.quantity)}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => handlePrintProductTag(item)}
                            title="Print Product Tag (38x15mm)"
                          >
                            <Tag className="w-3 h-3 mr-1" />
                            38x15
                          </Button>
                          
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() => {
                                  setSelectedItem(item);
                                  const parts = (item.import_month || "02/26").split("/");
                                  setImportMonth(parts[0] || "02");
                                  setImportYear(parts[1] || "26");
                                }}
                                title="Print Box Tag (50x25mm)"
                              >
                                <Box className="w-3 h-3 mr-1" />
                                50x25
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-md">
                              <DialogHeader>
                                <DialogTitle>Box Tag Settings</DialogTitle>
                                <DialogDescription>Set the month of import for this product's box tag</DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4 py-4">
                                <div>
                                  <p className="text-sm font-medium mb-2">{item.model_name}</p>
                                  <p className="text-sm text-slate-500">MRP: Rs. {item.mrp?.toLocaleString()}</p>
                                </div>
                                
                                <div className="space-y-2">
                                  <Label>Month of Import</Label>
                                  <div className="flex gap-2">
                                    <Select value={importMonth} onValueChange={setImportMonth}>
                                      <SelectTrigger className="w-32">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {MONTHS.map(m => (
                                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <Select value={importYear} onValueChange={setImportYear}>
                                      <SelectTrigger className="w-20">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {YEARS.map(y => (
                                          <SelectItem key={y} value={y}>20{y}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                                
                                <div className="flex gap-2 pt-2">
                                  <Button 
                                    variant="outline" 
                                    className="flex-1"
                                    onClick={handleUpdateImportMonth}
                                  >
                                    <Settings2 className="w-4 h-4 mr-2" />
                                    Save Default
                                  </Button>
                                  <Button 
                                    className="flex-1"
                                    onClick={() => {
                                      const month = `${importMonth}/${importYear}`;
                                      const url = `${process.env.REACT_APP_BACKEND_URL}/api/inventory/${item.sr_no}/box-tag?import_month=${encodeURIComponent(month)}`;
                                      window.open(url, '_blank');
                                    }}
                                  >
                                    <Box className="w-4 h-4 mr-2" />
                                    Print Tag
                                  </Button>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </TableCell>
                    </TableRow>
                    {expandedAudit === item.model_name && (
                      <TableRow className="hover:bg-transparent">
                        <TableCell colSpan={editMode ? 7 : 6} className="p-0">
                          <div className="bg-slate-50 border-t px-6 py-3">
                            <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
                              <Clock className="w-3 h-3" /> Inventory Movement Log — {item.model_name}
                            </p>
                            {!auditLogs[item.model_name] ? (
                              <p className="text-xs text-slate-400">Loading...</p>
                            ) : auditLogs[item.model_name].length === 0 ? (
                              <p className="text-xs text-slate-400">No movement history recorded yet</p>
                            ) : (
                              <div className="max-h-48 overflow-y-auto">
                                <table className="w-full text-xs">
                                  <thead className="sticky top-0 bg-slate-100">
                                    <tr>
                                      <th className="px-2 py-1 text-left font-medium text-slate-500">Date</th>
                                      <th className="px-2 py-1 text-left font-medium text-slate-500">Action</th>
                                      <th className="px-2 py-1 text-right font-medium text-slate-500">Change</th>
                                      <th className="px-2 py-1 text-right font-medium text-slate-500">After</th>
                                      <th className="px-2 py-1 text-left font-medium text-slate-500">Detail</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-200">
                                    {auditLogs[item.model_name].map((log, idx) => (
                                      <tr key={idx} className="hover:bg-white/50">
                                        <td className="px-2 py-1 text-slate-500 whitespace-nowrap">
                                          {log.timestamp ? new Date(log.timestamp).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                                        </td>
                                        <td className="px-2 py-1">
                                          <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                            log.action === 'deduction' ? 'bg-red-100 text-red-700' :
                                            log.action === 'void_restoration' ? 'bg-blue-100 text-blue-700' :
                                            log.action === 'rto_addition' ? 'bg-green-100 text-green-700' :
                                            log.action === 'manual_edit' ? 'bg-amber-100 text-amber-700' :
                                            log.action === 'new_sku_added' ? 'bg-emerald-100 text-emerald-700' :
                                            'bg-slate-100 text-slate-600'
                                          }`}>
                                            {log.action === 'deduction' ? 'PO Deduction' :
                                             log.action === 'void_restoration' ? 'Void Restore' :
                                             log.action === 'rto_addition' ? 'RTO Add' :
                                             log.action === 'manual_edit' ? 'Manual Edit' :
                                             log.action === 'new_sku_added' ? 'New SKU' :
                                             log.action === 'upload_skipped_qty_change' ? 'Upload Skip' :
                                             log.action || '—'}
                                          </span>
                                        </td>
                                        <td className={`px-2 py-1 text-right font-mono font-bold ${
                                          (log.quantity_change || 0) > 0 ? 'text-green-600' : (log.quantity_change || 0) < 0 ? 'text-red-600' : 'text-slate-400'
                                        }`}>
                                          {(log.quantity_change || 0) > 0 ? `+${log.quantity_change}` : log.quantity_change || '—'}
                                        </td>
                                        <td className="px-2 py-1 text-right font-mono">{log.quantity_after ?? '—'}</td>
                                        <td className="px-2 py-1 text-slate-600 max-w-[250px] truncate" title={log.detail}>{log.detail || '—'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            )}
          </ScrollArea>
        </Card>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedItems.size} selected item{selectedItems.size > 1 ? 's' : ''}? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" size="sm" onClick={() => setShowDeleteConfirm(false)} data-testid="cancel-delete-btn">
              Cancel
            </Button>
            <Button variant="destructive" size="sm" onClick={handleBulkDelete} disabled={deleting} data-testid="confirm-delete-btn">
              <Trash2 className="w-4 h-4 mr-1" />
              {deleting ? "Deleting..." : `Delete ${selectedItems.size} Items`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
